/**
 * Normalize gender values to single-letter codes (M/F/U).
 *
 * Accepts various formats from database:
 * - M, F, U (single letter - already normalized)
 * - Male, Female, Unknown (full word from legacy data)
 * - Case-insensitive variants
 *
 * @returns Normalized single-letter code (M/F/U) or null if value is empty/invalid.
 */
export const normalizeGender = (
  value: string | null | undefined
): 'M' | 'F' | 'U' | null => {
  if (!value || typeof value !== 'string') return null;

  const genderStr = value.trim().toLowerCase();

  if (genderStr === 'm' || genderStr === 'male') {
    return 'M';
  } else if (genderStr === 'f' || genderStr === 'female') {
    return 'F';
  } else if (genderStr === 'u' || genderStr === 'unknown') {
    return 'U';
  }

  return null;
};

/**
 * Get display label for gender code.
 *
 * @param genderCode - Single letter code (M/F/U) or full word
 * @returns Human-readable label (Male/Female/Unknown)
 */
export const getGenderLabel = (
  genderCode: string | null | undefined
): string => {
  const normalized = normalizeGender(genderCode);
  switch (normalized) {
    case 'M':
      return 'Male';
    case 'F':
      return 'Female';
    case 'U':
      return 'Unknown';
    default:
      return genderCode || '';
  }
};

// Format SSN as user types: 123-45-6789
export const formatSSN = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

// Format phone as user types: 555-123-4567
export const formatPhone = (value: string): string => {
  let digits = value.replace(/\D/g, '');
  // Remove +1 country code if present (11 digits starting with 1)
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  digits = digits.slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Format zip code as user types: 12345 or 12345-6789
export const formatZipCode = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

// Language mapping for Kinnser integration
// 1: English, 2: Spanish, 3: Chinese, 4: Russian, 5: Vietnamese, 6: Other/Unknown
export const mapLanguageToKey = (
  language?: string | null
): {
  language_key: '1' | '2' | '3' | '4' | '5' | '6';
  other_language?: string;
} => {
  if (!language) {
    return { language_key: '6' }; // Other/Unknown
  }

  const lang = language.toLowerCase().trim();

  switch (lang) {
    case 'english':
    case 'en':
      return { language_key: '1' };
    case 'spanish':
    case 'es':
      return { language_key: '2' };
    case 'chinese':
    case 'zh':
      return { language_key: '3' };
    case 'russian':
    case 'ru':
      return { language_key: '4' };
    case 'vietnamese':
    case 'vi':
      return { language_key: '5' };
    default:
      return { language_key: '6', other_language: language };
  }
};
// Transform intake data to RogerDoc patient creation format
export const transformIntakeToRogerDocPatient = (
  intake: {
    patient_first_name?: string | null;
    patient_last_name?: string | null;
    patient_primary_language?: string | null;
    agency_insurance?: { insurance_key?: string | null } | null;
    patient_dob?: string | Date | null;
    patient_gender?: string | null;
    patient_ssn?: string | null;
    patient_phone?: string | null;
    patient_email?: string | null;
    patient_street_address?: string | null;
    patient_city?: string | null;
    patient_state?: string | null;
    patient_zip_code?: string | null;
    patient_county?: string | null;
    patient_suffix?: string | null;
    medicaid_id?: string | null;
    kinnser_mrn?: string | null;
    kinnser_physician_id?: string | null;
    kinnser_clinician_id?: string | null;
    kinnser_clinical_care_manager_id?: string | null;
    kinnser_case_manager_id?: string | null;
    kinnser_oasis_approver_id?: string | null;
    kinnser_pt_approver_id?: string | null;
    kinnser_ot_approver_id?: string | null;
    kinnser_st_approver_id?: string | null;
    kinnser_admission_type?: string | null;
    kinnser_hcpcs_code_key?: string | null;
    kinnser_hh_cahps_opt_out?: boolean | null;
    kinnser_language_key?: string | null;
    kinnser_other_language?: string | null;
  },
  isTestPatient: boolean
) => {
  const patientFirstName = isTestPatient
    ? `Test${intake?.patient_first_name || ''}`
    : intake?.patient_first_name || '';
  const patientLastName = isTestPatient
    ? 'Dummy'
    : intake?.patient_last_name || '';

  // Get insurance_provider_id from agency insurance
  const insuranceProviderId = intake?.agency_insurance?.insurance_key || '';

  // Map language to language_key
  const { language_key, other_language } = mapLanguageToKey(
    intake?.patient_primary_language
  );

  // Split phone number (XXX-XXX-XXXX -> phone_a, phone_b, phone_c)
  const phoneParts = intake?.patient_phone?.split('-') || [];
  const phone_a = phoneParts[0] || '';
  const phone_b = phoneParts[1] || '';
  const phone_c = phoneParts[2] || '';

  // Split ZIP code (12345 or 12345-6789 -> zip_code, zip_ext)
  const zipParts = intake?.patient_zip_code?.split('-') || [];
  const zip_code = zipParts[0] || '';
  const zip_ext = zipParts[1] || '';

  // Map gender to male/female (handles both M/F/U and Male/Female/Unknown)
  let gender: 'male' | 'female' = 'female';
  const normalizedGender = normalizeGender(intake?.patient_gender);
  // Keep in mind that wellsky accepts only Male or Female, and RogerDoc backend treats
  // "male" as male, and all other values as female.
  if (normalizedGender === 'M') {
    gender = 'male';
  }

  // Format date of birth to MM/DD/YYYY
  let date_of_birth = '';
  if (intake?.patient_dob) {
    const dob =
      intake.patient_dob instanceof Date
        ? intake.patient_dob
        : new Date(intake.patient_dob);
    date_of_birth = dob.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  }

  // Split SSN (XXX-XX-XXXX -> ssn_part_a, ssn_part_b, ssn_part_c)
  const ssnParts = intake?.patient_ssn?.split('-') || [];
  const ssn_part_a = ssnParts[0] || '';
  const ssn_part_b = ssnParts[1] || '';
  const ssn_part_c = ssnParts[2] || '';

  return {
    first_name: patientFirstName,
    last_name: patientLastName,
    mrn: isTestPatient
      ? `T${Math.floor(Math.random() * 90000000) + 10000000}`
      : intake?.kinnser_mrn || '',
    physician_id: intake?.kinnser_physician_id || '',
    address: {
      street: intake?.patient_street_address || '',
      zip_code: zip_code,
      zip_ext: zip_ext,
      city: intake?.patient_city || '',
      state: intake?.patient_state || '',
      county: intake?.patient_county || '',
      timezone_key: '',
      latitude: '',
      longitude: '',
    },
    clinician_id: intake?.kinnser_clinician_id || '',
    clinical_care_manager_id: intake?.kinnser_clinical_care_manager_id || '',
    case_manager_id: intake?.kinnser_case_manager_id || '',
    oasis_approver_id: intake?.kinnser_oasis_approver_id || '',
    pt_approver_id: intake?.kinnser_pt_approver_id || '',
    ot_approver_id: intake?.kinnser_ot_approver_id || '',
    st_approver_id: intake?.kinnser_st_approver_id || '',
    insurance_provider_id: insuranceProviderId,
    medicaid_number: intake?.medicaid_id || '',
    date_of_birth: date_of_birth,
    suffix: intake?.patient_suffix || '',
    gender: gender as 'male' | 'female',
    ssn_part_a: ssn_part_a,
    ssn_part_b: ssn_part_b,
    ssn_part_c: ssn_part_c,
    language_key: language_key,
    other_language: other_language || '',
    email: intake?.patient_email || '',
    phone_a: phone_a,
    phone_b: phone_b,
    phone_c: phone_c,
    hcpcs_code_key: intake?.kinnser_hcpcs_code_key || '25',
    hh_cahps_opt_out: intake?.kinnser_hh_cahps_opt_out ?? true,
    admission_type: intake?.kinnser_admission_type || '1',
  };
};
